// Hostname -> brand authentication map for the STAFF softphone only.
//
// The public guest caller never authenticates; this map is used exclusively by
// the /staff surface. The hostname decides which identity realm the staff signs
// in against and how they reach sign-in ('hosted' = the brand's own sign-in
// page, 'portal' = the shared access portal). Names are generic on purpose; no
// identity-provider product name is exposed. Localhost/unknown hosts fall back
// to the VITE_AUTH_* build vars.

export type SignInEntry = 'hosted' | 'portal'

export interface BrandAuthConfig {
  brand: string
  authority: string
  realm: string
  clientId: string
  entry: SignInEntry
  portalUrl?: string
  portalAppId?: string
}

const HOST_MAP: Record<string, BrandAuthConfig> = {
  'qr-phone.com': {
    brand: 'qr-phone',
    authority: 'https://id.qr-phone.com',
    realm: 'qr-phone',
    clientId: 'qr-phone-staff',
    entry: 'hosted',
  },
  'www.qr-phone.com': {
    brand: 'qr-phone',
    authority: 'https://id.qr-phone.com',
    realm: 'qr-phone',
    clientId: 'qr-phone-staff',
    entry: 'hosted',
  },
  'qr-phone.roommatik.com': {
    brand: 'roommatik-qr-phone',
    authority: 'https://auth.roommatik.com',
    realm: 'roommatik',
    clientId: 'qr-phone-staff',
    entry: 'portal',
    portalUrl: 'https://portal.roommatik.com',
    portalAppId: 'qr-phone-staff',
  },
}

function fallbackConfig(): BrandAuthConfig {
  const env = import.meta.env
  return {
    brand: env.VITE_AUTH_BRAND || 'qr-phone',
    authority: env.VITE_AUTH_AUTHORITY || 'https://id.qr-phone.com',
    realm: env.VITE_AUTH_REALM || 'qr-phone',
    clientId: env.VITE_AUTH_CLIENT_ID || 'qr-phone-staff',
    entry: (env.VITE_AUTH_ENTRY as SignInEntry) || 'hosted',
    portalUrl: env.VITE_AUTH_PORTAL_URL || undefined,
    portalAppId: env.VITE_AUTH_PORTAL_APP_ID || undefined,
  }
}

let cached: BrandAuthConfig | null = null

export function resolveBrandConfig(): BrandAuthConfig {
  if (cached) return cached
  const host = window.location.hostname
  cached = HOST_MAP[host] || fallbackConfig()
  return cached
}
